// Packages
import React, { useCallback, useMemo, useRef, useState } from 'react';
import RunRateChart from './RunRateChart';
import downloadCSV from 'download-csv';

// Components
import { ITCOSubmission } from 'types';
import { useQueryData, useTcoGenerateProductTotals } from 'hooks';
import { tcoFieldsIds } from 'utils/defaultsAndConstants/constants';
import { KiteButton, KiteCard, KiteSelect } from '@kite/react-kite';
import BarChart from './BarChart';
import './TCODashboard.scss';
import { tcoFormattedPrice } from 'utils/defaultsAndConstants/tcoConstants';
import DiscountPrice from '../../../images/discount-price.svg';
import Payment from '../../../images/payment.svg';
import PaymentF from '../../../images/payment-f.svg';
import { useReactToPrint } from 'react-to-print';
import { useTcoDashboardExports } from 'hooks/useTcoDashboardExports';
import LegalDisclaimers from './LegalDisclaimers';

export interface ITCODashboard {
  tcoSubmissions: ITCOSubmission[];
}

const TCODashboard = ({ tcoSubmissions }: ITCODashboard) => {
  // =============================================
  // Helpers (Memo, CB, vars)
  // =============================================

  const { currentEstimate } = useQueryData();

  const [exportType, setExportType] = useState('');
  const mainContentRef = useRef<HTMLDivElement | null>(null);

  const { handleSummaryDownloadImage } = useTcoDashboardExports(mainContentRef);

  const values = ['PDF', 'JPG', 'Excel'];

  const exportValues = useMemo(() => {
    return values.map((p) => <option key={`id-${p}`}>{p}</option>);
  }, [values]);

  const handlePrint = useReactToPrint({
    content: () => mainContentRef.current,
    documentTitle: 'Summary',
    pageStyle: `

       @page {
         size: A4;
         margin: 0 30px;
         
        }
        .footer-print-container {
          display: block !important;
          page-break-before:avoid;
          position:fixed;
          bottom: 30px;
        
        }
        .dashboard-summary {
          display: flex;
          flex-direction:column;
          min-height:100vh;
        }
        
       `,
  });

  const onPrint = useCallback(() => {
    const summaryData = document.getElementById('summary-data');
    const summaryTitle = document.getElementById('summary-title');
    const legalDisclaimers = document.getElementById('legalDisclaimers');

    if (legalDisclaimers) {
      legalDisclaimers.style.display = 'block';
    }

    if (summaryData) {
      summaryData.style.gridTemplateColumns = '1fr';
      summaryData.style.rowGap = '1rem';
      summaryData.style.padding = '20px';

      const summaryCards = summaryData.querySelectorAll('.dashboard-total');

      summaryCards.forEach((card) => {
        (card as HTMLElement).style.width = '100%';
      });
    }

    if (summaryTitle) {
      summaryTitle.style.padding = '10px 20px 0px 20px';
    }
    if (handlePrint) {
      handlePrint();
    }

    // Reset styles after printing

    if (legalDisclaimers) {
      legalDisclaimers.style.display = 'none';
    }

    if (summaryData) {
      summaryData.style.gridTemplateColumns = 'repeat(3, 1fr)';
      summaryData.style.rowGap = '';
      summaryData.style.padding = '';

      const summaryCards = summaryData.querySelectorAll('.dashboard-total');

      summaryCards.forEach((card) => {
        // (card as HTMLElement).style.gridTemplateColumns = '';
        (card as HTMLElement).style.width = '';
      });
    }

    if (summaryTitle) {
      summaryTitle.style.padding = '';
    }
  }, [handlePrint]);

  const selectedExportType = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value } = e.target;
      setExportType(value);
    },
    []
  );

  // =============================================
  // Return
  // =============================================

  const { generateTcoProductTotals } = useTcoGenerateProductTotals();
  const { termsFieldId, miscItHoursCostFieldId, spectrumPromotionsFieldId } =
    tcoFieldsIds;

  const selectedTerm =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === termsFieldId)?.answer!
    ) || 0;

  const tcoOutputTotals = generateTcoProductTotals({
    format: false,
  });

  const miscItHoursCost =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === miscItHoursCostFieldId)?.answer!
    ) || 0;

  const spectrumPromotions =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === spectrumPromotionsFieldId)
        ?.answer!
    ) || 0;

  const {
    spectrumOneTimeCost,
    spectrumOngoingMonthlyCost,
    diyOneTimeCost,
    diyOngoingMonthlyCost,
    spectrumTotalCostOfOwnership,
    diyTotalCostOfOwnernship,
  } = useMemo(() => {
    const spectrumOneTimeCost =
      tcoOutputTotals.spectrumEquipmentCosts +
      tcoOutputTotals.spectrumCostOfCapital +
      tcoOutputTotals.spectrumImplementationCosts +
      tcoOutputTotals.spectrumTraining;

    const diyOneTimeCost =
      tcoOutputTotals.diyEquipmentCosts +
      tcoOutputTotals.diyCostOfCapital +
      tcoOutputTotals.diyImplementationCosts +
      tcoOutputTotals.diyTraining;

    const spectrumOngoingMonthlyCost =
      tcoOutputTotals.spectrumMonthlyDeviceManagementFee +
      tcoOutputTotals.spectrumConfigurationCostTotal +
      tcoOutputTotals.spectrumMonitoringCostTotal +
      tcoOutputTotals.spectrumTroubleshootingCostTotal;

    const diyOngoingMonthlyCost =
      tcoOutputTotals.diyMonthlyDeviceManagementFee +
      tcoOutputTotals.diyConfigurationCostTotal +
      tcoOutputTotals.diyMonitoringCostTotal +
      tcoOutputTotals.diyTroubleshootingCostTotal +
      tcoOutputTotals.licenseManagementFee +
      miscItHoursCost;

    const spectrumTotalCostOfOwnership =
      spectrumOneTimeCost +
      spectrumOngoingMonthlyCost * selectedTerm * 12 -
      spectrumPromotions;

    const diyTotalCostOfOwnernship =
      diyOneTimeCost + diyOngoingMonthlyCost * selectedTerm * 12;

    return {
      spectrumOneTimeCost,
      spectrumOngoingMonthlyCost,
      diyOneTimeCost,
      diyOngoingMonthlyCost,
      spectrumTotalCostOfOwnership,
      diyTotalCostOfOwnernship,
    };
  }, [tcoOutputTotals, selectedTerm, spectrumPromotions, miscItHoursCost]);

  const handleExcel = useCallback(() => {
    const csvRows = [];
    const headers = [
      'Totals',
      'Spectrum',
      'Diy',
      'Spectrum Saves You(Total)',
      'Spectrum Saves You(Annually)',
    ];

    const row1 = [
      'Total Costs',

      `"${String(
        tcoFormattedPrice(
          spectrumOneTimeCost + spectrumOngoingMonthlyCost * selectedTerm * 12
        )
      )}"`,

      `"${String(
        tcoFormattedPrice(
          diyOneTimeCost + diyOngoingMonthlyCost * selectedTerm * 12
        )
      )}"`,

      `"${String(
        tcoFormattedPrice(
          diyTotalCostOfOwnernship - spectrumTotalCostOfOwnership
        )
      )}"`,

      `"${String(
        tcoFormattedPrice(
          (diyTotalCostOfOwnernship - spectrumTotalCostOfOwnership) /
            selectedTerm
        )
      )}"`,
    ];

    const row2 = [
      'Promotional Discounts',
      `"${tcoFormattedPrice(spectrumPromotions)}"`,
      `"${tcoFormattedPrice(0)}"`,
    ];

    const row3 = [
      'Total Cost of Ownership',
      `"${tcoFormattedPrice(spectrumTotalCostOfOwnership)}"`,
      `"${tcoFormattedPrice(diyTotalCostOfOwnernship)}"`,
    ];

    csvRows.push(headers.join(','));

    csvRows.push(row1.join(','));
    csvRows.push(row2.join(','));
    csvRows.push(row3.join(','));
    const csvContent = csvRows.join('\n');

    try {
      // Trigger the download using download-csv
      downloadCSV(csvContent, '', 'Summary');
    } catch (error) {
      console.error('Error downloading CSV:', error);
    }

    return csvContent;
  }, [
    diyOneTimeCost,
    diyOngoingMonthlyCost,
    diyTotalCostOfOwnernship,
    selectedTerm,
    spectrumOneTimeCost,
    spectrumOngoingMonthlyCost,
    spectrumTotalCostOfOwnership,
    spectrumPromotions,
  ]);

  const handleExport = useCallback(() => {
    if (exportType === 'PDF') {
      onPrint();
    } else if (exportType === 'JPG') {
      handleSummaryDownloadImage('summary');
    } else if (exportType === 'Excel') {
      handleExcel();
    }
  }, [exportType, onPrint, handleSummaryDownloadImage, handleExcel]);

  return (
    <div className="tco-wrapper dashboard">
      <KiteCard>
        <div className="dashboard-header">
          <div className="dashboard-title">
            {currentEstimate?.name}

            <div className="dashboard-title-name">
              {currentEstimate?.customerName}
            </div>
          </div>

          <div className="dashboard-select">
            <span>Select Export Option </span>
            <KiteSelect
              id="dashboard-dropdown"
              name="current-export-value"
              onChange={selectedExportType}
              value={exportType!}
              placeholder={exportType ? '' : 'Make Selection'}
              className="dashboard-dropdown"
            >
              {exportValues}
            </KiteSelect>
          </div>
          <KiteButton size="small" onClick={() => handleExport()}>
            Export
          </KiteButton>
        </div>
      </KiteCard>

      <div className="dashboard-summary" ref={mainContentRef} id="summary">
        <h2 className="dashboard-summary-title" id="summary-title">
          Summary
        </h2>
        <div className="dashboard-summary-data" id="summary-data">
          <KiteCard className="dashboard-summary-data-card">
            <table id="summary-table">
              <thead>
                <tr>
                  <td>TOTALS</td>
                  <td className="label">SPECTRUM</td>
                  <td className="label">DIY</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="dashboard-summary-table-label">
                    {' '}
                    <img
                      src={Payment}
                      className="icon"
                      alt="payment-icon"
                      width="22"
                      height="22"
                    />
                    Total Costs
                  </td>
                  <td>
                    $
                    {tcoFormattedPrice(
                      spectrumOneTimeCost +
                        spectrumOngoingMonthlyCost * selectedTerm * 12
                    )}
                  </td>
                  <td>
                    $
                    {tcoFormattedPrice(
                      diyOneTimeCost + diyOngoingMonthlyCost * selectedTerm * 12
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="dashboard-summary-table-label">
                    <img
                      src={DiscountPrice}
                      className="icon"
                      alt="price"
                      width="22"
                      height="22"
                    />
                    Promotional Discounts
                  </td>
                  <td>-${tcoFormattedPrice(spectrumPromotions)}</td>
                  <td>--</td>
                </tr>
                <tr>
                  <td className="dashboard-summary-table-label">
                    <img
                      src={PaymentF}
                      className="icon"
                      alt="payment-f-icon"
                      width="22"
                      height="22"
                    />
                    Total Cost of Ownership
                  </td>
                  <td> ${tcoFormattedPrice(spectrumTotalCostOfOwnership)}</td>
                  <td>${tcoFormattedPrice(diyTotalCostOfOwnernship)}</td>
                </tr>
              </tbody>
            </table>
          </KiteCard>

          <KiteCard className="dashboard-total">
            <p>Spectrum Saves You</p>
            {Number(
              tcoFormattedPrice(
                diyTotalCostOfOwnernship - spectrumTotalCostOfOwnership
              )
            ) !== 0 ? (
              <div className="dashboard-total-value">
                $
                {tcoFormattedPrice(
                  diyTotalCostOfOwnernship - spectrumTotalCostOfOwnership
                )}
              </div>
            ) : (
              <div className="dashboard-total-value">--</div>
            )}
            <p className="dashboard-total-p">Total</p>
          </KiteCard>
          <KiteCard className="dashboard-total">
            <p>Spectrum Saves You</p>
            {Number(
              tcoFormattedPrice(
                diyTotalCostOfOwnernship - spectrumTotalCostOfOwnership
              )
            ) !== 0 ? (
              <div className="dashboard-total-value">
                $
                {tcoFormattedPrice(
                  (diyTotalCostOfOwnernship - spectrumTotalCostOfOwnership) /
                    selectedTerm
                )}
              </div>
            ) : (
              <div className="dashboard-total-value">--</div>
            )}

            <p className="dashboard-total-p">Annually</p>
          </KiteCard>
        </div>
        <LegalDisclaimers />
      </div>
      <h2 className="dashboard-summary-title">Reports</h2>
      <RunRateChart
        selectedTerm={selectedTerm}
        spectrumOneTimeCost={spectrumOneTimeCost}
        spectrumOngoingMonthlyCost={spectrumOngoingMonthlyCost}
        diyOneTimeCost={diyOneTimeCost}
        diyOngoingMonthlyCost={diyOngoingMonthlyCost}
        spectrumPromotions={spectrumPromotions}
      />
      <BarChart
        isOneTime={true}
        totalOutput={tcoOutputTotals}
        spectrumOneTimeCost={spectrumOneTimeCost}
        spectrumOngoingMonthlyCost={spectrumOngoingMonthlyCost}
        diyOneTimeCost={diyOneTimeCost}
        diyOngoingMonthlyCost={diyOngoingMonthlyCost}
      ></BarChart>
      <BarChart
        isOneTime={false}
        totalOutput={tcoOutputTotals}
        miscItHour={miscItHoursCost}
        spectrumOneTimeCost={spectrumOneTimeCost}
        spectrumOngoingMonthlyCost={spectrumOngoingMonthlyCost}
        diyOneTimeCost={diyOneTimeCost}
        diyOngoingMonthlyCost={diyOngoingMonthlyCost}
      ></BarChart>
      <LegalDisclaimers />
    </div>
  );
};

export default TCODashboard;
